import React from "react";
import { IMAGES } from "@/lib/brands";
import { Heart, ExternalLink, Apple, Play, Brain } from "lucide-react";

const PrisonReform = () => {
  return (
    <section
      id="prison-reform"
      className="relative bg-[color:var(--bg-dark)] text-white overflow-hidden"
      data-testid="prison-reform-section"
    >
      <div className="absolute inset-0 grid-lines-dark opacity-50 pointer-events-none" />
      <div className="absolute inset-0 noise pointer-events-none" />
      <div className="relative container-x section-pad">
        {/* Eyebrow + Headline */}
        <div className="max-w-4xl mb-12">
          <div className="eyebrow text-[color:var(--accent)] mb-4">
            ◆ Community Impact · National Feature
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tighter mb-6">
            Second Chance Program <br />
            <span className="text-[color:var(--accent)]">
              For Prison Reform &amp; Mental Health.
            </span>
          </h2>
          <p className="text-white/70 max-w-2xl text-sm md:text-base leading-relaxed">
            Our 501(c)(3) nonprofit — featured nationally — transforms lives by
            supporting formerly incarcerated individuals and championing mental
            health. Watch the story, then explore our Generational Health
            mobile app.
          </p>
        </div>

        {/* National Video — hero position */}
        <div
          className="relative border border-white/20 aspect-video w-full mb-16 overflow-hidden group"
          data-testid="national-video"
        >
          <iframe
            className="absolute inset-0 w-full h-full"
            src="https://www.youtube.com/embed/ZxeVpIbocSU?rel=0"
            title="Queen City Metal / Dale's Recycling — Moving Mountains"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>

        {/* Generational Health App Promo */}
        <div
          className="border-2 border-[color:var(--accent)] p-8 md:p-12 mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
          data-testid="generational-health-app"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[color:var(--accent)] grid place-items-center">
                <Brain size={18} />
              </div>
              <div className="eyebrow text-[color:var(--accent)]">
                Now On iOS &amp; Android
              </div>
            </div>
            <h3 className="font-display text-3xl md:text-5xl uppercase leading-none mb-4">
              Generational Health
            </h3>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-6">
              A mobile app for mental health — built as part of our Second
              Chance mission. Support, resources, and community wherever you
              are.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://apps.apple.com/us/app/generational-health/id6751965153"
                target="_blank"
                rel="noreferrer"
                className="hard-btn-inverse"
                data-testid="app-store-btn"
              >
                <Apple size={14} /> App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.generationalh.mobile.app&utm_source=na_Med"
                target="_blank"
                rel="noreferrer"
                className="hard-btn-inverse"
                data-testid="play-store-btn"
              >
                <Play size={14} /> Google Play
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[420px]">
              <div className="absolute -inset-3 border border-[color:var(--accent)]/40 pointer-events-none" />
              <video
                className="relative w-full aspect-video object-cover bg-black"
                src="https://customer-assets.emergentagent.com/job_integrated-yards/artifacts/2unrvof8_GH_16x9_RM.mp4"
                controls
                preload="metadata"
                playsInline
                data-testid="generational-health-video"
              />
              <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-white/60">
                Generational Health · Mental Health App
              </div>
            </div>
          </div>
        </div>

        {/* Pillars + Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.community}
                alt="Community hands together"
                className="w-full aspect-[4/5] object-cover"
                data-testid="prison-reform-image"
              />
              <div className="absolute -bottom-5 -right-5 bg-[color:var(--accent)] text-white px-6 py-4">
                <div className="font-mono text-[10px] uppercase tracking-widest">
                  501(c)(3)
                </div>
                <div className="font-display uppercase text-lg leading-none">
                  Nonprofit
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="eyebrow text-[color:var(--accent)] mb-4">
              ◆ What We Do
            </div>
            <h3 className="font-display text-3xl md:text-5xl uppercase leading-[0.95] tracking-tighter mb-6">
              Transforming Lives. <br />
              Reducing Recidivism.
            </h3>
            <p className="text-white/80 leading-relaxed mb-6">
              At the heart of our mission, the Second Chance Program is
              dedicated to transforming lives by supporting formerly
              incarcerated individuals as they rebuild and reintegrate into
              society. We advocate for meaningful prison reform, reduced
              recidivism, and real pathways to employment and dignity.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-10">
              {[
                "Rehabilitation & Education",
                "Post-Release Support",
                "Advocacy for Reform",
                "Community Engagement",
                "Family Reunification",
                "Workforce Development",
                "Mental Health Support",
                "Second Chance Employment",
              ].map((p, idx) => (
                <div
                  key={p}
                  className="flex items-center gap-3 py-3 border-t border-white/15"
                >
                  <span className="font-mono text-[10px] text-white/50">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm">{p}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://www.convergepay.com/hosted-payments?ssl_txn_auth_token=ZKbWdA9USIiLGtDEaQX9agAAAZ22PE1I"
                target="_blank"
                rel="noreferrer"
                className="hard-btn bg-[color:var(--accent)] text-white border-[color:var(--accent)] hover:bg-transparent hover:text-[color:var(--accent)]"
                data-testid="donate-btn"
              >
                <Heart size={14} /> Donate Now
              </a>
              <a
                href="https://www.youtube.com/watch?v=ZxeVpIbocSU"
                target="_blank"
                rel="noreferrer"
                className="hard-btn-inverse"
                data-testid="watch-video-btn"
              >
                Watch on YouTube <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrisonReform;
